import { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GroupAgentsTransfer } from './GroupAgentsTransfer';

const getDisplayName = (agent) => {
  const name = `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
  return name || agent.email || agent._id;
};

export const AgentGroupDetail = ({
  open,
  onClose,
  group,
  agents,
  loading,
  onUpdate,
  updateLoading,
  canManage,
}) => {
  const agentsMap = useMemo(() => {
    const map = {};
    (agents || []).forEach((a) => { map[a._id] = a; });
    return map;
  }, [agents]);

  const groupAgentObjects = useMemo(
    () =>
      (group?.members || [])
        .map((m) => agentsMap[m.agent_id])
        .filter(Boolean),
    [group, agentsMap]
  );

  const availableAgentObjects = useMemo(
    () => {
      const memberIds = new Set((group?.members || []).map((m) => m.agent_id));
      return (agents || []).filter((a) => !memberIds.has(a._id));
    },
    [agents, group]
  );

  const transferKey = group
    ? `${group.id}-${(group.members || []).map((m) => m.agent_id).join(',')}`
    : 'empty';

  if (!group) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        component="div"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}
      >
        <Box>
          <Typography variant="h3" sx={{ py: 1 }}>{group.name}</Typography>
          {group.description && (
            <Typography variant="body2" color="textSecondary">{group.description}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : canManage ? (
          <GroupAgentsTransfer
            key={transferKey}
            availableAgents={availableAgentObjects}
            groupAgents={groupAgentObjects}
            onUpdate={onUpdate}
            loading={updateLoading}
          />
        ) : (
          <List dense>
            {groupAgentObjects.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="textSecondary">
                      Sin miembros
                    </Typography>
                  }
                />
              </ListItem>
            ) : (
              groupAgentObjects.map((agent) => (
                <ListItem key={agent._id}>
                  <ListItemText
                    primary={getDisplayName(agent)}
                    secondary={agent.email}
                  />
                </ListItem>
              ))
            )}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};
